import React, { useEffect, useState } from "react";
import { CalendarClock, Trash2, Plus, Loader2, Clock, Car, BarChart3, Siren, Copy, Trophy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PageHeader, LoadingBlock, EmptyState, StatCard } from "../../components/app/ui";
import { adminApi, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function OnCallRoster() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [shifts, setShifts] = useState(null);
  const [staff, setStaff] = useState([]);
  const [report, setReport] = useState(null);
  const [insights, setInsights] = useState(null);
  const [sosRep, setSosRep] = useState(null);
  const [form, setForm] = useState({ user_id: "", weekday: 0, start: "09:00", end: "18:00" });
  const [busy, setBusy] = useState(false);
  const [copyForm, setCopyForm] = useState({ from_weekday: 0, to_weekdays: [] });
  const [copyBusy, setCopyBusy] = useState(false);

  const loadShifts = () => adminApi.staffShifts().then((d) => setShifts(d.shifts || [])).catch(() => setShifts([]));
  useEffect(() => {
    loadShifts();
    adminApi.staffList().then((d) => setStaff(d.staff || [])).catch(() => setStaff([]));
    adminApi.staffOnlineReport(14).then(setReport).catch(() => setReport({ staff: [] }));
    adminApi.vehicleClassInsights(30).then(setInsights).catch(() => setInsights(null));
    adminApi.sosResponseReport(8).then(setSosRep).catch(() => setSosRep(null));
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.user_id) return toast.error("Pick a staff member");
    setBusy(true);
    try { await adminApi.addStaffShift(form); toast.success("Shift added"); loadShifts(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await adminApi.deleteStaffShift(id); toast.success("Shift removed"); loadShifts(); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const toggleCopyDay = (d) => setCopyForm((f) => ({
    ...f, to_weekdays: f.to_weekdays.includes(d) ? f.to_weekdays.filter((x) => x !== d) : [...f.to_weekdays, d],
  }));
  const copyDay = async (e) => {
    e.preventDefault();
    if (copyForm.to_weekdays.length === 0) return toast.error("Pick at least one day to copy to");
    setCopyBusy(true);
    try {
      const res = await adminApi.copyDayShifts(copyForm);
      toast.success(res.added ? `Copied ${res.added} shift${res.added > 1 ? "s" : ""}` : "Nothing new to copy (duplicates skipped)");
      setCopyForm((f) => ({ ...f, to_weekdays: [] }));
      loadShifts();
    } catch (err) { toast.error(errorMessage(err)); } finally { setCopyBusy(false); }
  };

  return (
    <>
      <PageHeader title="On-call roster & staff hours" description="Set weekly shifts so SOS escalation only rings staff who are working. Admins are always on-call." />

      {isAdmin && (
        <form onSubmit={add} className="mb-6 kt-card flex flex-wrap items-end gap-3 p-5" data-testid="shift-form">
          <div className="space-y-1">
            <Label>Staff</Label>
            <select value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} className="h-10 rounded-xl border border-surface-300 bg-white px-3 text-sm" data-testid="shift-staff">
              <option value="">Select…</option>
              {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name} ({s.role})</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Day</Label>
            <select value={form.weekday} onChange={(e) => setForm((f) => ({ ...f, weekday: Number(e.target.value) }))} className="h-10 rounded-xl border border-surface-300 bg-white px-3 text-sm" data-testid="shift-day">
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div className="space-y-1"><Label>Start</Label><Input type="time" value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} className="w-32" data-testid="shift-start" /></div>
          <div className="space-y-1"><Label>End</Label><Input type="time" value={form.end} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} className="w-32" data-testid="shift-end" /></div>
          <Button type="submit" disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="shift-add">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Add shift</Button>
        </form>
      )}

      {isAdmin && (
        <form onSubmit={copyDay} className="mb-6 kt-card flex flex-wrap items-end gap-3 p-5" data-testid="copy-day-form">
          <div className="space-y-1">
            <Label>Copy shifts from</Label>
            <select value={copyForm.from_weekday} onChange={(e) => setCopyForm((f) => ({ ...f, from_weekday: Number(e.target.value), to_weekdays: f.to_weekdays.filter((d) => d !== Number(e.target.value)) }))} className="h-10 rounded-xl border border-surface-300 bg-white px-3 text-sm" data-testid="copy-from-day">
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>To these days</Label>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d, i) => i === copyForm.from_weekday ? null : (
                <button type="button" key={d} onClick={() => toggleCopyDay(i)} data-testid={`copy-to-${d}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${copyForm.to_weekdays.includes(i) ? "border-brand bg-brand text-white" : "border-surface-300 bg-white text-ink-soft hover:border-brand"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={copyBusy} className="rounded-full bg-ink text-white hover:bg-ink/90" data-testid="copy-day-btn">{copyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="mr-1 h-4 w-4" />} Copy to selected days</Button>
        </form>
      )}

      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-ink"><CalendarClock className="h-5 w-5 text-brand" /> Weekly shifts</h2>
      {shifts === null ? <LoadingBlock rows={3} /> : shifts.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No shifts set" text="Without shifts, every staff member is treated as on-call." />
      ) : (
        <div className="mb-10 grid gap-2" data-testid="shifts-list">
          {shifts.map((s) => (
            <div key={s.shift_id} className="flex items-center justify-between rounded-xl border border-surface-200 px-4 py-2.5 text-sm" data-testid={`shift-${s.shift_id}`}>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-ink">{s.user_name}</span>
                <span className="rounded-full bg-surface-100 px-2 py-0.5 text-xs text-ink-soft">{s.role}</span>
                <span className="text-ink-soft">{s.weekday_label} · {s.start}–{s.end} IST</span>
                {s.on_now && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">On call now</span>}
              </div>
              {isAdmin && <button onClick={() => remove(s.shift_id)} className="text-ink-muted hover:text-red-600" data-testid={`shift-del-${s.shift_id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-ink"><Siren className="h-5 w-5 text-red-600" /> SOS response (last 8 weeks)</h2>
      {sosRep && (sosRep.weeks || []).length > 0 && (
        <p className="mb-3 text-xs text-ink-muted" data-testid="sla-note">SLA target: acknowledge within <span className="font-semibold text-ink">{sosRep.sla_minutes} min</span>. Weeks above target (or with open alerts) are flagged.</p>
      )}
      {sosRep === null ? <LoadingBlock rows={2} /> : (sosRep.weeks || []).length === 0 ? (
        <EmptyState icon={Siren} title="No SOS yet" text="Weekly acknowledge times and responders will appear here." />
      ) : (
        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          <div className="overflow-x-auto rounded-2xl border border-surface-300" data-testid="sos-weeks-table">
            <table className="w-full text-sm">
              <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-4 py-3">Week</th><th className="px-4 py-3 text-right">SOS</th><th className="px-4 py-3 text-right">Ack'd</th><th className="px-4 py-3 text-right">Avg ack time</th></tr></thead>
              <tbody>
                {sosRep.weeks.map((w) => (
                  <tr key={w.week} className={`border-t border-surface-200 ${w.sla_breached ? "bg-red-50" : ""}`} data-testid={`sos-week-${w.week}`}>
                    <td className="px-4 py-3 font-medium text-ink">
                      <span className="inline-flex items-center gap-1.5">{w.week}
                        {w.sla_breached && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700" data-testid={`sla-breach-${w.week}`}><AlertTriangle className="h-3 w-3" /> SLA</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-ink-soft">{w.total}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{w.acknowledged}{w.unacknowledged ? <span className="text-red-600"> (+{w.unacknowledged} open)</span> : ""}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${w.sla_breached && w.avg_ack_minutes != null ? "text-red-600" : "text-ink"}`}>{w.avg_ack_minutes == null ? "—" : `${w.avg_ack_minutes} min`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-surface-300" data-testid="sos-responders-table">
            <table className="w-full text-sm">
              <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-4 py-3">Responder</th><th className="px-4 py-3 text-right">Handled</th><th className="px-4 py-3 text-right">Avg ack time</th></tr></thead>
              <tbody>
                {(sosRep.responders || []).length === 0 ? (
                  <tr><td className="px-4 py-6 text-center text-ink-muted" colSpan={3}>No acknowledgements yet.</td></tr>
                ) : sosRep.responders.map((r) => (
                  <tr key={r.name} className="border-t border-surface-200" data-testid={`sos-responder-${r.name}`}>
                    <td className="px-4 py-3 font-medium text-ink">{r.name}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{r.count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-ink">{r.avg_ack_minutes == null ? "—" : `${r.avg_ack_minutes} min`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-ink"><Trophy className="h-5 w-5 text-amber-500" /> Responder leaderboard</h2>
      {sosRep === null ? <LoadingBlock rows={2} /> : (sosRep.leaderboard || []).length === 0 ? (
        <EmptyState icon={Trophy} title="No ranked responders yet" text={`Staff appear here after at least ${sosRep?.leaderboard_min_responses || 2} acknowledgements, ranked by fastest average response.`} />
      ) : (
        <div className="mb-10 overflow-x-auto rounded-2xl border border-surface-300" data-testid="sos-leaderboard">
          <table className="w-full text-sm">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-4 py-3">Rank</th><th className="px-4 py-3">Responder</th><th className="px-4 py-3 text-right">Handled</th><th className="px-4 py-3 text-right">Avg ack time</th></tr></thead>
            <tbody>
              {sosRep.leaderboard.map((r) => (
                <tr key={r.name} className="border-t border-surface-200" data-testid={`sos-leader-${r.rank}`}>
                  <td className="px-4 py-3 font-bold text-ink">{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : `#${r.rank}`}</td>
                  <td className="px-4 py-3 font-medium text-ink">{r.name}</td>
                  <td className="px-4 py-3 text-right text-ink-soft">{r.count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-600">{r.avg_ack_minutes} min</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-ink"><Clock className="h-5 w-5 text-brand" /> Daily online hours (last 14 days)</h2>
      {report === null ? <LoadingBlock rows={2} /> : (report.staff || []).length === 0 ? (
        <EmptyState icon={Clock} title="No activity logged yet" text="Online hours accrue while staff have the dashboard open." />
      ) : (
        <div className="mb-10 overflow-x-auto rounded-2xl border border-surface-300" data-testid="hours-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-4 py-3">Staff</th><th className="px-4 py-3">Role</th><th className="px-4 py-3 text-right">Total hours</th><th className="px-4 py-3 text-right">Days active</th></tr></thead>
            <tbody>
              {report.staff.map((s) => (
                <tr key={s.user_id} className="border-t border-surface-200" data-testid={`hours-${s.user_id}`}>
                  <td className="px-4 py-3 font-medium text-ink">{s.user_name}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.role}</td>
                  <td className="px-4 py-3 text-right font-semibold text-ink">{s.total_hours} h</td>
                  <td className="px-4 py-3 text-right text-ink-soft">{Object.keys(s.days || {}).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-ink"><Car className="h-5 w-5 text-brand" /> Ride volume & fee income by vehicle class (30 days)</h2>
      {insights === null ? <LoadingBlock rows={2} /> : (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <StatCard label="Total rides" value={insights.totals.rides} icon={Car} />
            <StatCard label="Gross fares" value={money(insights.totals.gross_fare)} icon={BarChart3} tone="graphite" />
            <StatCard label="Platform fee income" value={money(insights.totals.fee_income)} icon={BarChart3} tone="brand" />
          </div>
          <div className="overflow-x-auto rounded-2xl border border-surface-300" data-testid="insights-table">
            <table className="w-full text-sm">
              <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-4 py-3">Vehicle class</th><th className="px-4 py-3 text-right">Fee/ride</th><th className="px-4 py-3 text-right">Rides</th><th className="px-4 py-3 text-right">Gross fares</th><th className="px-4 py-3 text-right">Fee income</th></tr></thead>
              <tbody>
                {insights.classes.map((c) => (
                  <tr key={c.vehicle_type} className="border-t border-surface-200" data-testid={`insight-${c.vehicle_type}`}>
                    <td className="px-4 py-3 font-medium text-ink">{c.label}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{money(c.fee_per_ride)}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{c.rides}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{money(c.gross_fare)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-ink">{money(c.fee_income)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
